import { useEffect, useId, useMemo, useState } from "react";
import type { MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemMessage } from "@/api/client";
import { formatMoney } from "@/lib/format";
import type { PayRun, PayrollRegisterReport } from "@/api/types";
import "./ReportsPayrollRegister.css";

type ViewMode = "run" | "range";
type LoadStatus = "loading" | "ready" | "error";

/** "2026-08-15" 30 days back from today, UTC. Seeds the date-range picker so switching to
 *  it shows a real window immediately, rather than two empty inputs and no report until a
 *  farmer fills both — the window is still theirs to change afterward. */
function defaultRangeStart(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultRangeEnd(): string {
  return new Date().toISOString().slice(0, 10);
}

/** "Aug 1 – Aug 15, 2026" — same helper shape as Labour.tsx's own `formatWindow`,
 *  duplicated rather than shared for the identical reason that file gives. */
function formatWindow(start: string, end: string): string {
  const asDate = (iso: string) => new Date(`${iso}T12:00:00Z`);
  const day = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  const dayAndYear = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${day.format(asDate(start))} – ${dayAndYear.format(asDate(end))}`;
}

/**
 * Payroll register — Reports' one screen for gross, each withholding, employer taxes and
 * net per employee, either for a single finalized pay run or summed across a date range.
 * ef-044-3, ADR 0009 §6 ("Reports is one picker" — a report renders inside the template at
 * `/reports/{slug}`, no area tab strip of its own; "All reports" below is the only way
 * back).
 *
 * TWO ROLE-BASED PICKERS, ONE VIEW SWITCH. The "View" combobox chooses which of the two
 * the API supports — a single finalized run, or a date range — and only the picker for the
 * active mode renders, same "no dead control" discipline as `Labour.tsx`'s own range form.
 * The "Pay run" picker lists FINALIZED, NON-VOIDED runs only (drafts and voided runs have
 * no money the server will return — see `PayrollRegisterReportEndpoints`'s own 400s), so a
 * farmer can never pick a run this screen would only refuse.
 */
export function ReportsPayrollRegister() {
  const viewId = useId();
  const runPickerId = useId();
  const fromId = useId();
  const toId = useId();

  const [view, setView] = useState<ViewMode>("run");

  const [runsStatus, setRunsStatus] = useState<LoadStatus>("loading");
  const [runsError, setRunsError] = useState<string | null>(null);
  const [runs, setRuns] = useState<PayRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string>("");

  const [from, setFrom] = useState(defaultRangeStart);
  const [to, setTo] = useState(defaultRangeEnd);

  const [report, setReport] = useState<PayrollRegisterReport | null>(null);
  const [reportStatus, setReportStatus] = useState<LoadStatus>("loading");
  const [reportError, setReportError] = useState<string | null>(null);

  // The finalized, non-voided run roster for the "Per pay run" picker — the server's own
  // rolling window (`GET /runs` with no range), same call `usePayRuns`'s own History uses.
  useEffect(() => {
    const controller = new AbortController();
    setRunsStatus("loading");
    demoApi
      .getPayRuns(undefined, controller.signal)
      .then((resp) => {
        if (controller.signal.aborted) return;
        const finalized = resp.runs.filter((r) => r.finalizedAt !== null && !r.voidedAt);
        setRuns(finalized);
        setRunsStatus("ready");
        setSelectedRunId((current) => current || finalized[0]?.id || "");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRunsStatus("error");
        setRunsError(problemMessage(err, "Could not load pay runs."));
      });
    return () => controller.abort();
  }, []);

  const rangeIsBackwards = from !== "" && to !== "" && to < from;

  // The report itself — re-fetched whenever the active mode's own selection changes.
  useEffect(() => {
    const controller = new AbortController();

    if (view === "run") {
      if (!selectedRunId) {
        setReport(null);
        setReportStatus(runsStatus === "loading" ? "loading" : "ready");
        return () => controller.abort();
      }
      setReportStatus("loading");
      demoApi
        .getPayrollRegister({ payRunId: selectedRunId }, controller.signal)
        .then((r) => {
          if (controller.signal.aborted) return;
          setReport(r);
          setReportStatus("ready");
          setReportError(null);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
          setReport(null);
          setReportStatus("error");
          setReportError(problemMessage(err, "Could not load the payroll register."));
        });
      return () => controller.abort();
    }

    if (!from || !to || rangeIsBackwards) {
      setReport(null);
      setReportStatus("ready");
      return () => controller.abort();
    }
    setReportStatus("loading");
    demoApi
      .getPayrollRegister({ from, to }, controller.signal)
      .then((r) => {
        if (controller.signal.aborted) return;
        setReport(r);
        setReportStatus("ready");
        setReportError(null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        setReport(null);
        setReportStatus("error");
        setReportError(problemMessage(err, "Could not load the payroll register."));
      });
    return () => controller.abort();
  }, [view, selectedRunId, from, to, rangeIsBackwards, runsStatus]);

  const totals = useMemo(() => {
    const rows = report?.rows ?? [];
    return {
      gross: rows.reduce((sum, r) => sum + r.gross, 0),
      piecePay: rows.reduce((sum, r) => sum + (r.piecePay ?? 0), 0),
      ficaEmployee: rows.reduce((sum, r) => sum + r.ficaEmployee, 0),
      federalWh: rows.reduce((sum, r) => sum + r.federalWh, 0),
      stateWh: rows.reduce((sum, r) => sum + r.stateWh, 0),
      net: rows.reduce((sum, r) => sum + r.net, 0),
      ficaEmployer: rows.reduce((sum, r) => sum + r.ficaEmployer, 0),
    };
  }, [report]);

  const status: LoadStatus = view === "run" && runsStatus === "loading" ? "loading" : reportStatus;
  const rows = report?.rows ?? [];
  const noFinalizedRuns = view === "run" && runsStatus === "ready" && runs.length === 0;
  // ef-060-8 @ui-check: the "Piece pay" column exists only when some row in this window has piece pay —
  // a register without it keeps exactly the columns it always had.
  const showPiece = rows.some((r) => (r.piecePay ?? 0) > 0);
  const isEmpty = status === "ready" && rows.length === 0 && !noFinalizedRuns && !rangeIsBackwards;

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device AreaTabs.tsx already uses rather than threading a
    // `navigate` prop through every screen this job touches: `pushState` doesn't fire
    // `popstate` on its own, so App.tsx's existing listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  return (
    <main className="payroll-register">
      <PageHeader
        title="Payroll register"
        subtitle="Gross, each withholding, employer taxes and net — per pay run or a date range"
      />

      <a href="/reports" className="payroll-register__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="payroll-register__panel"
        aria-labelledby="payroll-register-heading"
        data-state={isEmpty || noFinalizedRuns ? "empty" : status}
      >
        <h2 id="payroll-register-heading" className="payroll-register__heading">
          Payroll register
        </h2>

        <div className="payroll-register__controls">
          <div className="payroll-register__field">
            <label className="payroll-register__label" htmlFor={viewId}>
              View
            </label>
            <select
              id={viewId}
              className="payroll-register__select"
              value={view}
              onChange={(event) => setView(event.target.value as ViewMode)}
            >
              <option value="run">Per pay run</option>
              <option value="range">Date range</option>
            </select>
          </div>

          {view === "run" ? (
            <div className="payroll-register__field">
              <label className="payroll-register__label" htmlFor={runPickerId}>
                Pay run
              </label>
              <select
                id={runPickerId}
                className="payroll-register__select"
                value={selectedRunId}
                disabled={runsStatus !== "ready" || runs.length === 0}
                onChange={(event) => setSelectedRunId(event.target.value)}
              >
                {runs.length === 0 && <option value="">No finalized runs yet</option>}
                {runs.map((r) => (
                  <option key={r.id} value={r.id}>
                    {formatWindow(r.periodStart, r.periodEnd)}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div className="payroll-register__field">
                <label className="payroll-register__label" htmlFor={fromId}>
                  From
                </label>
                <input
                  id={fromId}
                  className="payroll-register__input"
                  type="date"
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                />
              </div>
              <div className="payroll-register__field">
                <label className="payroll-register__label" htmlFor={toId}>
                  To
                </label>
                <input
                  id={toId}
                  className="payroll-register__input"
                  type="date"
                  value={to}
                  aria-invalid={rangeIsBackwards || undefined}
                  onChange={(event) => setTo(event.target.value)}
                />
              </div>
            </>
          )}
        </div>

        {view === "range" && rangeIsBackwards && (
          <p className="payroll-register__field-error" role="alert">
            The end of the period cannot be before its start.
          </p>
        )}

        {status === "loading" && <StateMessage tone="loading" title="Loading the payroll register…" />}

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the payroll register."
            detail={reportError ?? runsError ?? undefined}
          />
        )}

        {noFinalizedRuns && (
          <StateMessage
            tone="empty"
            title="No finalized pay runs yet."
            detail="A run appears here once it has been finalized on the Pay runs screen — a draft or voided run has no money to report."
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="Nothing to show for this window."
            detail="Pick a finalized run, or a date range that covers one."
          />
        )}

        {status === "ready" && rows.length > 0 && (
          <>
            <div
              className="payroll-register__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="payroll-register-heading"
            >
              <table className="payroll-register__table ruled-rows">
                <caption className="payroll-register__caption">
                  {rows.length} {rows.length === 1 ? "line" : "lines"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Employee</th>
                    {showPiece && <th scope="col" className="payroll-register__num">Piece pay</th>}
                    <th scope="col" className="payroll-register__num">Gross</th>
                    <th scope="col" className="payroll-register__num">FICA</th>
                    <th scope="col" className="payroll-register__num">Federal W/H</th>
                    <th scope="col" className="payroll-register__num">State W/H</th>
                    <th scope="col" className="payroll-register__num">Net</th>
                    <th scope="col" className="payroll-register__num">Employer taxes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.payRunId}-${r.employeeId}`}>
                      <th scope="row" className="payroll-register__name">
                        {r.employeeName}
                      </th>
                      {showPiece && (
                        <td className="payroll-register__num payroll-register__amount">{formatMoney(r.piecePay ?? 0)}</td>
                      )}
                      <td className="payroll-register__num payroll-register__amount">{formatMoney(r.gross)}</td>
                      <td className="payroll-register__num payroll-register__amount">{formatMoney(r.ficaEmployee)}</td>
                      <td className="payroll-register__num payroll-register__amount">{formatMoney(r.federalWh)}</td>
                      <td className="payroll-register__num payroll-register__amount">{formatMoney(r.stateWh)}</td>
                      <td className="payroll-register__num payroll-register__amount">{formatMoney(r.net)}</td>
                      <td className="payroll-register__num payroll-register__amount">{formatMoney(r.ficaEmployer)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">Total</th>
                    {showPiece && (
                      <td className="payroll-register__num payroll-register__amount">{formatMoney(totals.piecePay)}</td>
                    )}
                    <td className="payroll-register__num payroll-register__amount">{formatMoney(totals.gross)}</td>
                    <td className="payroll-register__num payroll-register__amount">{formatMoney(totals.ficaEmployee)}</td>
                    <td className="payroll-register__num payroll-register__amount">{formatMoney(totals.federalWh)}</td>
                    <td className="payroll-register__num payroll-register__amount">{formatMoney(totals.stateWh)}</td>
                    <td className="payroll-register__num payroll-register__amount">{formatMoney(totals.net)}</td>
                    <td className="payroll-register__num payroll-register__amount">{formatMoney(totals.ficaEmployer)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <Why>
              <strong>FICA</strong> is this employee&rsquo;s own Social Security and Medicare
              withholding — <strong>Employer taxes</strong> is the employer&rsquo;s matching
              share of the same tax, never withheld from anyone&rsquo;s check.{" "}
              <strong>Net</strong> is gross less FICA, Federal and State withholding exactly —
              the same identity the database itself enforces on every finalized line.
            </Why>
          </>
        )}
      </section>
    </main>
  );
}
